import type { AppType } from '@ember/platform-api'
import { hc } from 'hono/client'

/**
 * Token types as defined in ember_context.md.
 * BaseToken: Post-login identity (sub, email, is_verified).
 * ContextToken: Post-context-pick (realm, role, context_id, vertical_id, membership_id).
 */
export type BaseToken = string
export type ContextToken = string

/**
 * Base URL for the Platform API.
 * In development, defaults to http://localhost:8787.
 */
const BASE_URL = import.meta.env['VITE_API_URL'] ?? 'http://localhost:8787'

/**
 * Factory to create a Hono RPC client with optional authorization headers.
 * Returns a typed client instance augmented with token injection methods.
 */
function createClient(token?: string) {
  const instance = hc<AppType>(BASE_URL, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })

  return {
    /**
     * The underlying Hono RPC client.
     */
    api: (instance as any).api,
    /**
     * Returns a new client instance injected with a Base Token (Identity).
     */
    withBaseToken(token: BaseToken) {
      return createClient(token)
    },
    /**
     * Returns a new client instance injected with a Context Token (Realm/Context).
     */
    withContextToken(token: ContextToken) {
      return createClient(token)
    },
  }
}

/**
 * The single, typed Hono RPC client for the Ember Platform API.
 */
export const emberClient = createClient()
